import React, { createContext, useContext, useReducer, useCallback } from 'react';

// Initial state for title management
const initialState = {
  title: 'Elevate Your Digital Presence',
  subtitle: '',
  description: 'Professional digital solutions and services',
  keywords: 'digital solutions, web development, technology',
  isLoading: false,
  error: null,
  template: '{title} | TukeWIN',
  siteName: 'TukeWIN',
  ogImage: '/assets/og-image.jpg',
  twitterCard: 'summary_large_image'
};

// Action types
const TITLE_ACTIONS = {
  SET_TITLE: 'SET_TITLE',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  RESET_TITLE: 'RESET_TITLE',
  SET_META_DATA: 'SET_META_DATA'
};

// Reducer function
const titleReducer = (state, action) => {
  switch (action.type) {
    case TITLE_ACTIONS.SET_TITLE:
      return {
        ...state,
        title: action.payload.title || state.title,
        subtitle: action.payload.subtitle || '',
        description: action.payload.description || state.description,
        keywords: action.payload.keywords || state.keywords,
        isLoading: false,
        error: null
      };
    
    case TITLE_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
        error: null
      };
    
    case TITLE_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false
      };
    
    case TITLE_ACTIONS.RESET_TITLE:
      return {
        ...initialState,
        template: state.template,
        siteName: state.siteName,
        ogImage: state.ogImage,
        twitterCard: state.twitterCard
      };
    
    case TITLE_ACTIONS.SET_META_DATA:
      return {
        ...state,
        ...action.payload
      };
    
    default:
      return state;
  }
};

// Create context
const TitleContext = createContext();

// Custom hook to use title context
export const useTitleContext = () => {
  const context = useContext(TitleContext);
  if (!context) {
    throw new Error('useTitleContext must be used within a TitleProvider');
  }
  return context;
};

// Title provider component
export const TitleProvider = ({ children }) => {
  const [state, dispatch] = useReducer(titleReducer, initialState);

  // Format title using template
  const formatTitle = useCallback((title, useTemplate = true) => {
    if (!useTemplate || !state.template) {
      return title;
    }
    return state.template.replace('{title}', title);
  }, [state.template]);

  // Set title with optional metadata
  const setTitle = useCallback((titleData) => {
    if (typeof titleData === 'string') {
      dispatch({
        type: TITLE_ACTIONS.SET_TITLE,
        payload: { title: titleData }
      });
    } else {
      dispatch({
        type: TITLE_ACTIONS.SET_TITLE,
        payload: titleData
      });
    }
  }, []);

  // Set loading state
  const setLoading = useCallback((isLoading) => {
    dispatch({
      type: TITLE_ACTIONS.SET_LOADING,
      payload: isLoading
    });
  }, []);

  // Set error state
  const setError = useCallback((error) => {
    dispatch({
      type: TITLE_ACTIONS.SET_ERROR,
      payload: error
    });
  }, []);

  // Reset to default title
  const resetTitle = useCallback(() => {
    dispatch({
      type: TITLE_ACTIONS.RESET_TITLE
    });
  }, []);

  // Update meta data configuration
  const updateMetaConfig = useCallback((config) => {
    dispatch({
      type: TITLE_ACTIONS.SET_META_DATA,
      payload: config
    });
  }, []);

  // Generate full URL for Open Graph
  const getFullUrl = useCallback((path = '') => {
    const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
    return `${baseUrl}${path}`;
  }, []);

  // Generate structured data for SEO
  const getStructuredData = useCallback((type = 'WebSite') => {
    const baseData = {
      '@context': 'https://schema.org',
      '@type': type,
      name: state.siteName,
      description: state.description,
      url: getFullUrl()
    };

    if (type === 'Article') {
      return {
        ...baseData,
        '@type': 'Article',
        headline: state.title,
        description: state.description,
        author: {
          '@type': 'Organization',
          name: state.siteName
        },
        publisher: {
          '@type': 'Organization',
          name: state.siteName
        }
      };
    }

    return baseData;
  }, [state.siteName, state.description, state.title, getFullUrl]);

  const value = {
    // State
    ...state,
    
    // Computed values
    formattedTitle: formatTitle(state.title),
    fullTitle: state.subtitle ? `${state.title} - ${state.subtitle}` : state.title,
    
    // Actions
    setTitle,
    setLoading,
    setError,
    resetTitle,
    updateMetaConfig,
    
    // Utilities
    formatTitle,
    getFullUrl,
    getStructuredData
  };

  return (
    <TitleContext.Provider value={value}>
      {children}
    </TitleContext.Provider>
  );
};

export default TitleContext;
